import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { bankFilePaymentsAPI, SplitAndMapPayInsToInvoicesResponse } from 'redux/bankFilePaymentsAPI';
import { showSnackbar } from 'redux/snackbar';
import i18n from 'i18next';
import type { MerchantAdminState } from 'redux/store';
import { fetchBankFilePayment } from '../bankFilePaymentsGlobalState';

type MarkAsManuallyRefundedParams = {
  bankFilePaymentId: string;
  remainingUnmappedAmount: number;
};

export const markAsManuallyRefunded = createAsyncThunk<
  SplitAndMapPayInsToInvoicesResponse,
  MarkAsManuallyRefundedParams,
  { state: MerchantAdminState }
>(
  'bankFilePayments/markAsManuallyRefundedDialog/mark',
  async ({ bankFilePaymentId, remainingUnmappedAmount }: MarkAsManuallyRefundedParams, { dispatch }) => {
    const result = await bankFilePaymentsAPI.bankFilePayments.markAsManuallyRefunded({
      bankFilePaymentId,
      remainingUnmappedAmount,
    });
    dispatch(fetchBankFilePayment({ bankFilePaymentId }));
    dispatch(
      showSnackbar(i18n.t('bankFilePayments.actions.markAsManuallyRefunded.success', { bankFilePaymentId }), {
        severity: 'success',
      }),
    );
    return result;
  },
);

type DialogState = {
  isFetching: boolean;
  success: boolean | null;
  error: any;
};

type SliceState = Record<string, DialogState | undefined>;

const markAsManuallyRefundedDialogSlice = createSlice({
  name: 'bankFilePayments/actions/markAsManuallyRefundedDialog',
  initialState: {} as SliceState,
  reducers: {
    reset: (state, { payload: { bankFilePaymentId } }: { payload: { bankFilePaymentId: string } }) => {
      state[bankFilePaymentId] = undefined;
    },
  },
  extraReducers: builder => {
    builder.addCase(markAsManuallyRefunded.pending, (state, { meta: { arg } }) => {
      state[arg.bankFilePaymentId] = { isFetching: true, success: null, error: null };
    });
    builder.addCase(markAsManuallyRefunded.fulfilled, (state, { meta: { arg } }) => {
      state[arg.bankFilePaymentId] = { isFetching: false, success: true, error: null };
    });
    builder.addCase(markAsManuallyRefunded.rejected, (state, { meta: { arg }, error }) => {
      state[arg.bankFilePaymentId] = { isFetching: false, success: false, error };
    });
  },
});

export const {
  actions: { reset },
  reducer: markAsManuallyRefundedDialogReducer,
} = markAsManuallyRefundedDialogSlice;
